/* eslint-disable no-bitwise */
/**
 * Перепаковка WebM/Opus → OGG/Opus без перекодирования звука.
 *
 * Перенос из панели Chatwoot (app/javascript/dashboard/components/widgets/WootWriter/utils/webmOpusToOgg.js, MIT):
 * Chrome/Android пишет звук в контейнере WebM, а Telegram и WhatsApp принимают голосовое только как
 * OGG/Opus. Из WebM достаются «сырые» пакеты Opus и укладываются в OGG. Ссылки: RFC 8794 (EBML),
 * RFC 3533 (OGG), RFC 7845 (Opus в OGG).
 */

const EBML_IDS = {
  Segment: 0x18538067,
  SegmentInfo: 0x1549a966,
  Tracks: 0x1654ae6b,
  TrackEntry: 0xae,
  CodecPrivate: 0x63a2,
  Audio: 0xe1,
  SamplingFrequency: 0xb5,
  Channels: 0x9f,
  Cluster: 0x1f43b675,
  SimpleBlock: 0xa3,
  BlockGroup: 0xa0,
  Block: 0xa1,
} as const;

const MASTER_ELEMENTS = new Set<number>([
  0x1a45dfa3, // заголовок EBML
  EBML_IDS.Segment, EBML_IDS.SegmentInfo, EBML_IDS.Tracks, EBML_IDS.TrackEntry,
  EBML_IDS.Audio, EBML_IDS.Cluster, EBML_IDS.BlockGroup,
]);

interface VInt { value: number; length: number }

/** Число переменной длины (размер элемента). */
function readVint(data: Uint8Array, pos: number): VInt | null {
  if (pos >= data.length) return null;
  const first = data[pos];
  if (first === 0) return null;
  let len = 1;
  let mask = 0x80;
  while (len <= 8 && !(first & mask)) {
    len += 1;
    mask >>= 1;
  }
  if (len > 8 || pos + len > data.length) return null;
  let value = first & (mask - 1);
  for (let i = 1; i < len; i += 1) value = value * 256 + data[pos + i];
  return { value, length: len };
}

/** Номер элемента (маркерные биты сохраняются). */
function readElementId(data: Uint8Array, pos: number): { id: number; length: number } | null {
  if (pos >= data.length) return null;
  const first = data[pos];
  if (first === 0) return null;
  let len = 1;
  let mask = 0x80;
  while (len <= 4 && !(first & mask)) {
    len += 1;
    mask >>= 1;
  }
  if (len > 4 || pos + len > data.length) return null;
  let id = first;
  for (let i = 1; i < len; i += 1) id = id * 256 + data[pos + i];
  return { id, length: len };
}

function readUintBE(data: Uint8Array, offset: number, length: number): number {
  let v = 0;
  for (let i = 0; i < length; i += 1) v = v * 256 + data[offset + i];
  return v;
}

function readFloatBE(data: Uint8Array, offset: number, length: number): number {
  if (length !== 4 && length !== 8) return NaN;
  const view = new DataView(new ArrayBuffer(length));
  for (let i = 0; i < length; i += 1) view.setUint8(i, data[offset + i]);
  return length === 4 ? view.getFloat32(0) : view.getFloat64(0);
}

/** Кадр Opus из SimpleBlock/Block. Склейка кадров (lacing) не поддерживается — MediaRecorder её не делает. */
function extractFrame(data: Uint8Array, offset: number, end: number): Uint8Array | null {
  const track = readVint(data, offset);
  if (!track) return null;
  // дальше: 2 байта относительного времени и байт флагов
  const pos = offset + track.length + 3;
  if (pos >= end) return null;
  return data.slice(pos, end);
}

export interface ParsedWebM {
  channels: number;
  sampleRate: number;
  codecPrivate: Uint8Array | null;
  frames: Uint8Array[];
}

export function parseWebM(buffer: ArrayBuffer): ParsedWebM {
  const data = new Uint8Array(buffer);
  const result: ParsedWebM = { channels: 1, sampleRate: 48000, codecPrivate: null, frames: [] };

  const walk = (start: number, end: number): void => {
    let pos = start;
    while (pos < end) {
      const idRes = readElementId(data, pos);
      if (!idRes) break;
      pos += idRes.length;
      const sizeRes = readVint(data, pos);
      if (!sizeRes) break;
      pos += sizeRes.length;
      // «Неизвестный размер» (все единицы) = до конца родителя.
      const maxVint = 2 ** (7 * sizeRes.length) - 1;
      const elEnd = sizeRes.value === maxVint ? end : Math.min(pos + sizeRes.value, end);

      if (MASTER_ELEMENTS.has(idRes.id)) {
        walk(pos, elEnd);
      } else if (idRes.id === EBML_IDS.Channels) {
        result.channels = readUintBE(data, pos, sizeRes.value);
      } else if (idRes.id === EBML_IDS.SamplingFrequency) {
        result.sampleRate = readFloatBE(data, pos, sizeRes.value);
      } else if (idRes.id === EBML_IDS.CodecPrivate) {
        result.codecPrivate = data.slice(pos, elEnd);
      } else if (idRes.id === EBML_IDS.SimpleBlock || idRes.id === EBML_IDS.Block) {
        const frame = extractFrame(data, pos, elEnd);
        if (frame && frame.length > 0) result.frames.push(frame);
      }
      pos = elEnd;
    }
  };

  walk(0, data.length);
  return result;
}

// ── OGG ──

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i << 24;
    for (let j = 0; j < 8; j += 1) c = ((c << 1) ^ (c & 0x80000000 ? 0x04c11db7 : 0)) >>> 0;
    t[i] = c;
  }
  return t;
})();

function oggCrc32(bytes: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < bytes.length; i += 1) crc = (CRC_TABLE[((crc >>> 24) ^ bytes[i]) & 0xff] ^ (crc << 8)) >>> 0;
  return crc;
}

/** Одна страница OGG: 0x02 — начало потока, 0x04 — конец. */
function createOggPage(headerType: number, granule: number, serial: number, seq: number, packets: Uint8Array[]): Uint8Array {
  const segTable: number[] = [];
  let dataLen = 0;
  packets.forEach((pkt) => {
    let rem = pkt.length;
    while (rem >= 255) {
      segTable.push(255);
      rem -= 255;
    }
    segTable.push(rem);
    dataLen += pkt.length;
  });
  const hdrLen = 27 + segTable.length;
  const page = new Uint8Array(hdrLen + dataLen);
  const dv = new DataView(page.buffer);
  page.set([0x4f, 0x67, 0x67, 0x53]); // "OggS"
  page[4] = 0;
  page[5] = headerType;
  dv.setUint32(6, granule % 0x100000000, true);
  dv.setUint32(10, Math.floor(granule / 0x100000000) % 0x100000000, true);
  dv.setUint32(14, serial, true);
  dv.setUint32(18, seq, true);
  dv.setUint32(22, 0, true);
  page[26] = segTable.length;
  segTable.forEach((v, i) => { page[27 + i] = v; });
  let off = hdrLen;
  packets.forEach((pkt) => { page.set(pkt, off); off += pkt.length; });
  dv.setUint32(22, oggCrc32(page), true);
  return page;
}

/** Длительность кадра Opus (мс) по номеру конфигурации в первом байте пакета. */
const OPUS_FRAME_MS = [
  10, 20, 40, 60, 10, 20, 40, 60, 10, 20, 40, 60, 10, 20, 10, 20,
  2.5, 5, 10, 20, 2.5, 5, 10, 20, 2.5, 5, 10, 20, 2.5, 5, 10, 20,
];

/** Сколько отсчётов 48 кГц в пакете Opus. */
function opusPacketSamples(pkt: Uint8Array): number {
  if (!pkt || pkt.length === 0) return 960;
  const toc = pkt[0];
  const config = (toc >> 3) & 0x1f;
  const code = toc & 0x03;
  const perFrame = ((OPUS_FRAME_MS[config] || 20) * 48000) / 1000;
  let frames: number;
  if (code <= 1) frames = code + 1;
  else if (code === 2) frames = 2;
  else frames = pkt.length >= 2 ? pkt[1] & 0x3f : 1;
  return perFrame * frames;
}

function buildOpusHead(channels: number, sampleRate: number, preSkip: number): Uint8Array {
  const buf = new Uint8Array(19);
  const dv = new DataView(buf.buffer);
  buf.set(new TextEncoder().encode('OpusHead'));
  buf[8] = 1;
  buf[9] = channels;
  dv.setUint16(10, preSkip, true);
  dv.setUint32(12, sampleRate, true);
  dv.setInt16(16, 0, true);
  buf[18] = 0;
  return buf;
}

function buildOpusTags(): Uint8Array {
  const vendor = new TextEncoder().encode('saleus');
  const buf = new Uint8Array(8 + 4 + vendor.length + 4);
  const dv = new DataView(buf.buffer);
  buf.set(new TextEncoder().encode('OpusTags'));
  dv.setUint32(8, vendor.length, true);
  buf.set(vendor, 12);
  dv.setUint32(12 + vendor.length, 0, true);
  return buf;
}

const MAX_FRAMES_PER_PAGE = 50;
const MAX_SEGMENTS_PER_PAGE = 255;

/** Уже OGG? (начинается с «OggS») */
export function isOgg(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53;
}

/** Содержимое Blob. Запасной путь через FileReader — для старых WebView и тестовой среды без Blob.arrayBuffer. */
export function readBlob(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === 'function') return b.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(b);
  });
}

/** WebM/Opus → OGG/Opus. Уже готовый OGG возвращается как есть. */
export async function remuxWebmToOgg(webm: Blob): Promise<Blob> {
  const buffer = await readBlob(webm);
  if (isOgg(new Uint8Array(buffer))) return webm;

  const { channels, sampleRate, codecPrivate, frames } = parseWebM(buffer);
  if (frames.length === 0) throw new Error('В записи не нашлось звуковых пакетов Opus');

  // Начальный сдвиг берём из CodecPrivate (там лежит тот же OpusHead).
  let preSkip = 312;
  if (codecPrivate && codecPrivate.length >= 12) {
    const magic = new TextDecoder().decode(codecPrivate.slice(0, 8));
    if (magic === 'OpusHead') {
      preSkip = new DataView(codecPrivate.buffer, codecPrivate.byteOffset, codecPrivate.length).getUint16(10, true);
    }
  }

  const serial = (Math.random() * 0x100000000) >>> 0;
  let seq = 0;
  const pages: Uint8Array[] = [];
  pages.push(createOggPage(0x02, 0, serial, seq, [buildOpusHead(channels, sampleRate, preSkip)]));
  seq += 1;
  pages.push(createOggPage(0x00, 0, serial, seq, [buildOpusTags()]));
  seq += 1;

  let granule = 0;
  let idx = 0;
  while (idx < frames.length) {
    const packets: Uint8Array[] = [];
    let segs = 0;
    while (idx < frames.length && packets.length < MAX_FRAMES_PER_PAGE) {
      const pkt = frames[idx];
      const pktSegs = Math.floor(pkt.length / 255) + 1;
      if (segs + pktSegs > MAX_SEGMENTS_PER_PAGE && packets.length > 0) break;
      packets.push(pkt);
      segs += pktSegs;
      granule += opusPacketSamples(pkt);
      idx += 1;
    }
    const isLast = idx >= frames.length;
    pages.push(createOggPage(isLast ? 0x04 : 0x00, granule, serial, seq, packets));
    seq += 1;
  }

  const total = pages.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  pages.forEach((p) => { out.set(p, off); off += p.length; });
  return new Blob([out], { type: 'audio/ogg' });
}
